import {
  Box,
  BoxProps,
  Checkbox,
  Chip,
  FormControlLabel,
  makeStyles,
  OutlinedInputProps,
  TextField,
  TextFieldProps,
} from '@material-ui/core';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@material-ui/lab/Autocomplete';
// eslint-disable-next-line import/no-cycle
import { fetchDepartments } from '@reducers/departments';
import { departmentsSelector } from '@selectors/departments';
import { urlAgentConsole } from '@src/utils/adminRoutes';
import { Department, DepartmentState, DepartmentType } from '@types';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { departmentWithoutAgents } from '../AgentToolbar/Utils';
import { DynamicAutocompleteType } from '../CustomMaterialUI/DynamicAutocomplete';
import DotStatus from './DotStatus';

export type DepartmentValue =
  | Department
  | { botId?: number; departmentId?: number; name?: string };

export type DepartmentChangeEventHandler<Multiple extends boolean = false> = (
  // eslint-disable-next-line @typescript-eslint/ban-types
  event: React.ChangeEvent<{}> | null,
  value: Multiple extends true ? Department[] : Department | null,
  reason: AutocompleteChangeReason,
  details?: AutocompleteChangeDetails<Department>
) => void;

export type DepartmentSelectProps<Multiple extends boolean = boolean> = Omit<
  DynamicAutocompleteType<Department, Multiple>,
  | 'options'
  | 'getOptionLabel'
  | 'filterOptions'
  | 'renderInput'
  | 'onChange'
  | 'value'
  | 'error'
> & {
  label?: boolean | string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: Multiple;
  value: Multiple extends true ? DepartmentValue[] : DepartmentValue | null;
  onChange: DepartmentChangeEventHandler<Multiple>;
  onBlur?(): void;
  filterOptions?: (option: Department) => boolean;
  disableInactiveOptions?: boolean;
  clearable?: boolean;
  standardName?: 'generalDepartment' | 'standardDepartment';
  extraOptions?: {
    botId?: number | string;
    departmentId?: number | string;
    [key: string]: unknown;
  }[];
  fullWidth?: boolean;
  margin?: 'dense' | 'normal';
  error?: string | boolean;
  withStatus?: boolean;
  keyName?: 'botId' | 'departmentId';
  renderTags?(
    value: Department[],
    getTagProps: AutocompleteGetTagProps
  ): React.ReactNode;
  sx?: BoxProps['sx'];
  smallInput?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  className?: string;
  inputProps?: TextFieldProps['inputProps'];
};

const useStyles = makeStyles({
  paper: {
    width: 'fit-content',
  },
  input: {
    height: '7px',
    fontSize: '12px',
    paddingRight: 23,
  },
  inputRoot: {
    paddingRight: '0px !important',
    paddingLeft: '2px !important',
    width: '100%',
  },
  endAdornment: {
    right: '8px !important',
  },
  inputBase: {
    paddingRight: '25px !important',
  },
});

declare function DepartmentSelectComponent<Multiple extends boolean = boolean>(
  props: DepartmentSelectProps<Multiple>
): JSX.Element;

const DepartmentSelect: React.FC<
  DepartmentSelectProps & { intl: IntlShape }
> = ({
  label,
  placeholder,
  disabled,
  multiple,
  value,
  standardName,
  onChange,
  onBlur,
  filterOptions = () => true,
  disableInactiveOptions,
  clearable,
  extraOptions,
  fullWidth,
  margin,
  error,
  keyName = 'botId',
  renderTags,
  withStatus = true,
  sx,
  intl: { formatMessage },
  smallInput = false,
  getOptionSelected,
  renderInput: renderInputProp,
  open,
  onClose,
  className,
  inputProps,
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const classes = useStyles();

  const { items, loading } = useSelector(departmentsSelector);

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  const getFilterOptions = useCallback(
    () =>
      [...((extraOptions as Department[]) ?? []), ...items].filter(dep =>
        filterOptions ? filterOptions(dep) : true
      ),
    [filterOptions, extraOptions, items]
  );

  const [options, setOptions] = useState<Department[]>(getFilterOptions());

  useEffect(() => {
    let filteredOptions = getFilterOptions();
    if (pathname === urlAgentConsole) {
      filteredOptions = [
        ...[departmentWithoutAgents],
        ...filteredOptions,
      ] as Department[];
    }
    setOptions(filteredOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, items]);

  useEffect(() => {
    if (
      !Array.isArray(value) &&
      value !== null &&
      value?.[keyName] === undefined &&
      options.length > 0
    ) {
      const department = options.find(
        item => item.departmentId === Number(value?.departmentId)
      );

      if (department || !options[0]?.departmentId)
        onChange(null, department || options[0], 'select-option');
    }
  }, [value, options, onChange, keyName]);

  const renderInput = useCallback(
    ({
      InputProps: renderInputProps,
      ...params
    }: AutocompleteRenderInputParams) => {
      const option = Array.isArray(value)
        ? undefined
        : options.find(dep => dep[keyName] === value?.[keyName]);

      return (
        <TextField
          {...params}
          label={label ? <FormattedMessage id="Department.Label" /> : label}
          placeholder={
            placeholder || formatMessage({ id: 'Department.Placeholder' })
          }
          variant="outlined"
          InputProps={{
            ...(renderInputProps as Partial<OutlinedInputProps>),
            ...(inputProps as Partial<OutlinedInputProps>),
            startAdornment:
              option && withStatus ? (
                <span>{renderStatus(option)}</span>
              ) : (
                renderInputProps.startAdornment
              ),
            margin: 'dense',
            className: `${classes.inputBase}`,
          }}
          InputLabelProps={{ margin: 'dense' }}
          margin={margin || 'dense'}
          error={!!error}
          helperText={error}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      value,
      options,
      label,
      placeholder,
      formatMessage,
      inputProps,
      withStatus,
      margin,
      error,
      keyName,
    ]
  );

  const getOptionLabel = useCallback(
    (option: Department) => {
      let name =
        option?.name ||
        options.find(dep => dep?.[keyName] === option?.[keyName])?.name;

      if (!name) return '';

      if (name === 'BotStandardServices')
        name = standardName || 'standardDepartment';

      return formatMessage({ id: 'Department.Item' }, { name });
    },
    [formatMessage, keyName, options, standardName]
  );

  const renderOption = useCallback(
    (option: Department, state: AutocompleteRenderOptionState) => {
      const name =
        option.name === 'BotStandardServices'
          ? standardName || 'standardDepartment'
          : option.name;

      return (
        <>
          {!multiple && withStatus && renderStatus(option)}
          <span>
            {multiple ? (
              <FormControlLabel
                label={
                  <FormattedMessage id="Department.Item" values={{ name }} />
                }
                control={<Checkbox checked={state.selected} size="small" />}
              />
            ) : (
              <FormattedMessage id="Department.Item" values={{ name }} />
            )}
          </span>
        </>
      );
    },
    [multiple, standardName, withStatus]
  );

  const getDefaultOptionSelected = <T extends Department>(
    option: T,
    value_?: T
  ) => option?.[keyName] === value_?.[keyName];

  return (
    <Box sx={sx}>
      <Autocomplete
        className={['department-select', className || ''].join(' ')}
        classes={{
          paper: classes.paper,
          input: smallInput ? classes.input : '',
          inputRoot: smallInput ? classes.inputRoot : '',
          endAdornment: classes.endAdornment,
        }}
        multiple={multiple}
        autoComplete
        autoHighlight
        fullWidth={fullWidth}
        disableClearable={!clearable}
        disableCloseOnSelect={multiple}
        size="small"
        options={options}
        groupBy={groupByParent}
        renderTags={renderTags || renderTagsDefault}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected || getDefaultOptionSelected}
        getOptionDisabled={
          disableInactiveOptions ? getOptionDisabled : undefined
        }
        renderInput={renderInputProp || renderInput}
        renderOption={renderOption}
        value={value as Department | Department[]}
        onChange={onChange}
        onBlur={onBlur}
        loading={loading === 'pending'}
        disabled={disabled}
        open={open}
        onClose={onClose}
      />
    </Box>
  );
};

export function renderStatus(option?: Department) {
  if (option?.type === DepartmentType.STANDARD) return null;
  if (option?.state) {
    return <DotStatus status={option?.state} />;
  }
  return null;
}

function renderTagsDefault(
  value: Department[],
  getTagProps: AutocompleteGetTagProps
) {
  return value.map((option, index) => (
    <Chip
      key={option.id || option.departmentId}
      size="small"
      variant="default"
      label={
        <FormattedMessage id="Department.Item" values={{ name: option.name }} />
      }
      {...getTagProps({ index })}
    />
  ));
}

export function departmentLeafs(option: Department) {
  return !option.childrens;
}

function groupByParent(option?: Department) {
  return `${option?.parentName || ''}`;
}

function getOptionDisabled(option: Department) {
  return option?.state === DepartmentState.INACTIVE;
}

export default injectIntl(
  DepartmentSelect
) as unknown as typeof DepartmentSelectComponent;
